import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Users,
  Phone,
  MapPin,
  DollarSign,
  Eye,
  RefreshCw,
  Package,
  CheckCircle,
  XCircle,
  Clock,
  CreditCard,
  Truck,
  Tag,
  Save,
  Download,
  FileText,
  ShoppingBasket,
  Warehouse,
  ArrowRight,
  Key,
  UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'gcash', label: 'GCash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'check', label: 'Check' }
];

const NASCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Modals
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isViewCustomerOpen, setIsViewCustomerOpen] = useState(false);
  const [isBasketModalOpen, setIsBasketModalOpen] = useState(false);
  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false);
  
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [customerBasket, setCustomerBasket] = useState(null);
  
  // Credentials form
  const [credentialsForm, setCredentialsForm] = useState({
    username: '',
    password: ''
  });
  
  const [customerForm, setCustomerForm] = useState({
    name: '',
    phone: '',
    address: '',
    username: '',
    password: '',
    notes: ''
  });
  
  // Store created login details to show to admin
  const [createdLoginDetails, setCreatedLoginDetails] = useState(null);
  const [showLoginDetails, setShowLoginDetails] = useState(false);
  
  const [orderForm, setOrderForm] = useState({
    customerId: '',
    items: [],
    totalAmount: 0,
    deliveryDate: '',
    deliveryAddress: '',
    notes: '',
    paymentTerms: 'cod'
  });
  
  const [paymentForm, setPaymentForm] = useState({
    customerId: '',
    orderId: '',
    amount: 0,
    paymentMethod: 'cash',
    referenceNumber: '',
    notes: ''
  });
  
  const [newOrderItem, setNewOrderItem] = useState({
    productId: '',
    name: '',
    quantity: 0,
    unit: 'sack',
    pricePerUnit: 0
  });
  
  // Custom Pricing state
  const [customerPrices, setCustomerPrices] = useState([]);
  const [showPricingSection, setShowPricingSection] = useState(false);
  const [newPriceForm, setNewPriceForm] = useState({
    productId: '',
    customPrice: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [customersRes, productsRes] = await Promise.all([
        axios.get(`${API}/api/nas-customer/admin/customers`),
        axios.get(`${API}/api/nas/products`)
      ]);
      setCustomers(customersRes.data);
      setProducts(productsRes.data);
      // Get pending payments from old endpoint (if exists) or new customer orders
      try {
        const paymentsRes = await axios.get(`${API}/api/nas/customer-payments/pending`);
        setPendingPayments(paymentsRes.data);
      } catch {
        setPendingPayments([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  // Customer CRUD
  const handleSaveCustomer = async () => {
    if (!customerForm.name) {
      toast.error('Customer name is required');
      return;
    }
    if (!customerForm.username) {
      toast.error('Username is required for login');
      return;
    }
    if (!editingCustomer && !customerForm.password) {
      toast.error('Password is required for login');
      return;
    }
    
    setIsSaving(true);
    try {
      if (editingCustomer) {
        // Update existing customer
        const updateData = {
          name: customerForm.name,
          phone: customerForm.phone,
          address: customerForm.address,
          notes: customerForm.notes,
          username: customerForm.username
        };
        // Only include password if it's being changed
        if (customerForm.password) {
          updateData.password = customerForm.password;
        }
        const response = await axios.put(`${API}/api/nas-customer/admin/customers/${editingCustomer.id}`, updateData);
        toast.success('Customer updated');
        if (response.data.new_password) {
          setCreatedLoginDetails({
            username: customerForm.username,
            password: response.data.new_password
          });
          setShowLoginDetails(true);
        }
      } else {
        // Create new customer with login
        const response = await axios.post(`${API}/api/nas-customer/admin/customers`, {
          name: customerForm.name,
          phone: customerForm.phone,
          address: customerForm.address,
          username: customerForm.username,
          password: customerForm.password,
          notes: customerForm.notes
        });
        toast.success('Customer created successfully!');
        // Show login details
        setCreatedLoginDetails(response.data.login_details);
        setShowLoginDetails(true);
      }
      resetCustomerForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCustomer = async (customer) => {
    if (!window.confirm(`Delete "${customer.name}"? This cannot be undone.`)) return;
    
    try {
      await axios.post(`${API}/api/nas-customer/admin/customers/${customer.id}/suspend`);
      toast.success('Customer suspended');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const openEditCustomer = (customer) => {
    setCustomerForm({
      name: customer.name,
      contactPerson: customer.contactPerson || '',
      phone: customer.phone || '',
      address: customer.address || '',
      email: customer.email || '',
      creditLimit: customer.creditLimit || 0,
      notes: customer.notes || ''
    });
    setEditingCustomer(customer);
    setIsAddCustomerOpen(true);
  };

  const resetCustomerForm = () => {
    setCustomerForm({
      name: '',
      phone: '',
      address: '',
      username: '',
      password: '',
      notes: ''
    });
    setEditingCustomer(null);
    setIsAddCustomerOpen(false);
  };

  // View customer details
  const viewCustomerDetails = async (customer) => {
    try {
      const response = await axios.get(`${API}/api/nas-customer/admin/customers/${customer.id}`);
      setCustomerDetails(response.data);
      setCustomerPrices(response.data.custom_prices || []);
      setShowPricingSection(false);
      setIsViewCustomerOpen(true);
    } catch (error) {
      toast.error('Failed to load customer details');
    }
  };

  // View customer basket (all delivered orders)
  const viewCustomerBasket = async (customer) => {
    try {
      setSelectedCustomer(customer);
      const response = await axios.get(`${API}/api/nas-customer/admin/customer-basket/${customer.id}`);
      setCustomerBasket(response.data);
      setIsBasketModalOpen(true);
    } catch (error) {
      console.error('Error loading basket:', error);
      toast.error('Failed to load customer basket');
    }
  };

  // Custom Pricing functions
  const loadCustomerPrices = async (customerId) => {
    try {
      const response = await axios.get(`${API}/api/nas-customer/admin/customer-prices/${customerId}`);
      setCustomerPrices(response.data);
    } catch (error) {
      console.error('Failed to load custom prices:', error);
    }
  };

  const handleAddCustomPrice = async () => {
    if (!newPriceForm.productId || !newPriceForm.customPrice) {
      toast.error('Select product and enter price');
      return;
    }
    
    setIsSaving(true);
    try {
      await axios.post(`${API}/api/nas-customer/admin/customer-prices`, {
        customer_id: customerDetails.id,
        product_id: newPriceForm.productId,
        custom_price: parseFloat(newPriceForm.customPrice)
      });
      toast.success('Custom price saved!');
      await loadCustomerPrices(customerDetails.id);
      setNewPriceForm({ productId: '', customPrice: 0 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save custom price');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveCustomPrice = async (productId) => {
    setIsSaving(true);
    try {
      await axios.delete(`${API}/api/nas-customer/admin/customer-prices/${customerDetails.id}/${productId}`);
      toast.success('Custom price removed');
      await loadCustomerPrices(customerDetails.id);
    } catch (error) {
      toast.error('Failed to remove custom price');
    } finally {
      setIsSaving(false);
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.name || 'Unknown Product';
  };

  const getProductDefaultPrice = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.sellingPricePerSack || product?.wholesalePrice || 0;
  };

  // Order functions
  const openOrderModal = (customer) => {
    setSelectedCustomer(customer);
    setOrderForm({
      customerId: customer.id,
      items: [],
      totalAmount: 0,
      deliveryDate: new Date().toISOString().split('T')[0],
      deliveryAddress: customer.address || '',
      notes: '',
      paymentTerms: 'cod'
    });
    setIsOrderModalOpen(true);
  };

  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setNewOrderItem({
        productId: product.id,
        name: product.name,
        quantity: 0,
        unit: 'sack',
        pricePerUnit: product.sellingPricePerSack || product.wholesalePrice || 0,
        maxStock: product.bodegaStock || 0
      });
    }
  };

  const handleAddOrderItem = () => {
    if (!newOrderItem.productId || newOrderItem.quantity <= 0) {
      toast.error('Select product and enter quantity');
      return;
    }
    
    if (newOrderItem.quantity > newOrderItem.maxStock) {
      toast.error(`Insufficient stock. Available: ${newOrderItem.maxStock}`);
      return;
    }
    
    const itemTotal = newOrderItem.quantity * newOrderItem.pricePerUnit;
    const updatedItems = [...orderForm.items, { ...newOrderItem, total: itemTotal }];
    const totalAmount = updatedItems.reduce((sum, item) => sum + item.total, 0);
    
    setOrderForm({ ...orderForm, items: updatedItems, totalAmount });
    setNewOrderItem({ productId: '', name: '', quantity: 0, unit: 'sack', pricePerUnit: 0 });
  };

  const handleRemoveOrderItem = (index) => {
    const updatedItems = orderForm.items.filter((_, i) => i !== index);
    const totalAmount = updatedItems.reduce((sum, item) => sum + item.total, 0);
    setOrderForm({ ...orderForm, items: updatedItems, totalAmount });
  };

  const handleCreateOrder = async () => {
    if (orderForm.items.length === 0) {
      toast.error('Add at least one item');
      return;
    }
    
    setIsSaving(true);
    try {
      // Use admin endpoint to create order on behalf of customer
      const orderData = {
        customer_id: orderForm.customerId,
        items: orderForm.items.map(item => ({
          product_id: item.productId,
          quantity: item.quantity,
          price_per_unit: item.pricePerUnit
        })),
        notes: orderForm.notes,
        payment_method: orderForm.paymentTerms,
        delivery_address: orderForm.deliveryAddress
      };
      
      const response = await axios.post(`${API}/api/nas-customer/admin/orders/create-for-customer`, orderData);
      toast.success(`Order ${response.data.delivery_code} created for ${response.data.customer_name}!`);
      setIsOrderModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setIsSaving(false);
    }
  };

  // Payment functions
  const openPaymentModal = (customer) => {
    setSelectedCustomer(customer);
    setPaymentForm({
      customerId: customer.id,
      orderId: '',
      amount: 0,
      paymentMethod: 'cash',
      referenceNumber: '',
      notes: ''
    });
    setIsPaymentModalOpen(true);
  };

  // Credentials functions
  const openCredentialsModal = (customer) => {
    setSelectedCustomer(customer);
    setCredentialsForm({
      username: customer.username || '',
      password: ''
    });
    setIsCredentialsModalOpen(true);
  };

  const handleSetCredentials = async () => {
    if (!credentialsForm.username || !credentialsForm.password) {
      toast.error('Please enter both username and password');
      return;
    }
    
    if (credentialsForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsSaving(true);
    try {
      await axios.post(`${API}/api/nas-customer/admin/customers/${selectedCustomer.id}/set-credentials`, credentialsForm);
      toast.success(`Portal access set for ${selectedCustomer.name}!`);
      setIsCredentialsModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set credentials');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveCredentials = async (customer) => {
    if (!window.confirm(`Remove portal access for ${customer.name}?`)) return;
    
    try {
      await axios.delete(`${API}/api/nas-customer/admin/customers/${customer.id}/credentials`);
      toast.success(`Portal access removed for ${customer.name}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove credentials');
    }
  };

  const handleRecordPayment = async () => {
    if (paymentForm.amount <= 0) {
      toast.error('Enter payment amount');
      return;
    }
    
    setIsSaving(true);
    try {
      await axios.post(`${API}/api/nas/customer-payments`, paymentForm);
      toast.success('Payment recorded - pending confirmation');
      setIsPaymentModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmPayment = async (paymentId) => {
    try {
      await axios.put(`${API}/api/nas/customer-payments/${paymentId}/confirm`);
      toast.success('Payment confirmed');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm payment');
    }
  };

  const handleRejectPayment = async (paymentId) => {
    if (!window.confirm('Reject this payment?')) return;
    try {
      await axios.put(`${API}/api/nas/customer-payments/${paymentId}/reject`);
      toast.success('Payment rejected');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject payment');
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Customers</h1>
          <p className="text-slate-500">Manage customers, orders, and payments ({customers.length} customers)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={isLoading}>
            <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          </Button>
          <Button onClick={() => setIsAddCustomerOpen(true)} className="bg-green-600 hover:bg-green-700">
            <Plus size={18} className="mr-2" />
            Add Customer
          </Button>
        </div>
      </div>

      <Tabs defaultValue="customers" className="space-y-4">
        <TabsList>
          <TabsTrigger value="customers">
            <Users size={16} className="mr-2" />
            Customers
          </TabsTrigger>
          <TabsTrigger value="pending-payments" className="relative">
            <Clock size={16} className="mr-2" />
            Pending Payments
            {pendingPayments.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                {pendingPayments.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Customers Tab */}
        <TabsContent value="customers" className="space-y-4">
          {/* Search */}
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardContent>
          </Card>

          {/* Customers List */}
          <div className="grid gap-4">
            {isLoading ? (
              <div className="text-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto"></div>
              </div>
            ) : filteredCustomers.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="p-10 text-center">
                  <Users className="mx-auto text-slate-300 mb-4" size={48} />
                  <p className="text-slate-500">No customers yet. Add your first customer!</p>
                </CardContent>
              </Card>
            ) : (
              filteredCustomers.map(customer => (
                <Card key={customer.id} className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-800">{customer.name}</h3>
                          {customer.balance > 0 && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                              Balance: ₱{customer.balance?.toLocaleString()}
                            </span>
                          )}
                          {customer.balance === 0 && customer.totalOrders > 0 && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                              Paid
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                          {customer.phone && (
                            <span className="flex items-center gap-1">
                              <Phone size={14} />
                              {customer.phone}
                            </span>
                          )}
                          {customer.address && (
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              {customer.address}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <DollarSign size={14} />
                            Total: ₱{customer.totalOrders?.toLocaleString() || 0}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 flex-wrap">
                        <Button variant="outline" size="sm" onClick={() => viewCustomerDetails(customer)}>
                          <Eye size={16} className="mr-1" /> View
                        </Button>
                        <Button variant="outline" size="sm" className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100" onClick={() => viewCustomerBasket(customer)}>
                          <ShoppingBasket size={16} className="mr-1" /> Basket
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openOrderModal(customer)}>
                          <Package size={16} className="mr-1" /> New Order
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openPaymentModal(customer)}>
                          <CreditCard size={16} className="mr-1" /> Payment
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className={customer.has_portal_access ? "bg-green-50 border-green-200 text-green-700" : "bg-slate-50"}
                          onClick={() => openCredentialsModal(customer)}
                          data-testid={`credentials-btn-${customer.id}`}
                        >
                          <Key size={16} className="mr-1" />
                          {customer.has_portal_access ? 'Portal' : 'Set Login'}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openEditCustomer(customer)}>
                          <Edit size={16} />
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteCustomer(customer)}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Pending Payments Tab */}
        <TabsContent value="pending-payments" className="space-y-4">
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock size={20} />
                Payments Pending Confirmation
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingPayments.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No pending payments</p>
              ) : (
                <div className="space-y-3">
                  {pendingPayments.map(payment => (
                    <div key={payment.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div>
                        <p className="font-medium">{payment.customerName}</p>
                        <p className="text-sm text-slate-600">
                          ₱{payment.amount?.toLocaleString()} via {payment.paymentMethod}
                          {payment.referenceNumber && ` (Ref: ${payment.referenceNumber})`}
                        </p>
                        <p className="text-xs text-slate-500">
                          Recorded by {payment.recordedBy} on {new Date(payment.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-green-600" onClick={() => handleConfirmPayment(payment.id)}>
                          <CheckCircle size={16} className="mr-1" /> Confirm
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleRejectPayment(payment.id)}>
                          <XCircle size={16} className="mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Customer Modal */}
      <Dialog open={isAddCustomerOpen} onOpenChange={setIsAddCustomerOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Customer/Shop Name *</Label>
              <Input
                value={customerForm.name}
                onChange={(e) => setCustomerForm({...customerForm, name: e.target.value})}
                placeholder="e.g., Juan's Agri Store"
              />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input
                value={customerForm.phone}
                onChange={(e) => setCustomerForm({...customerForm, phone: e.target.value})}
                placeholder="e.g., 09171234567"
              />
            </div>
            <div>
              <Label>Address *</Label>
              <Textarea
                value={customerForm.address}
                onChange={(e) => setCustomerForm({...customerForm, address: e.target.value})}
                placeholder="Complete delivery address"
                rows={2}
              />
            </div>
            
            {/* Login Credentials Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                🔐 Login Credentials
                {editingCustomer && <span className="text-xs text-slate-500 font-normal">(Leave password blank to keep current)</span>}
              </h4>
              <div className="mb-3">
                <Label>Username *</Label>
                <Input
                  value={customerForm.username}
                  onChange={(e) => setCustomerForm({...customerForm, username: e.target.value.replace(/\s/g, '')})}
                  placeholder="e.g., JuanStore"
                />
                <p className="text-xs text-slate-500 mt-1">Customer will use this to login at the customer portal</p>
              </div>
              <div>
                <Label>{editingCustomer ? 'New Password (optional)' : 'Password *'}</Label>
                <Input
                  type="password"
                  value={customerForm.password}
                  onChange={(e) => setCustomerForm({...customerForm, password: e.target.value})}
                  placeholder={editingCustomer ? "Enter new password to change" : "Enter password"}
                />
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={customerForm.notes}
                onChange={(e) => setCustomerForm({...customerForm, notes: e.target.value})}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetCustomerForm}>Cancel</Button>
            <Button onClick={handleSaveCustomer} disabled={isSaving} className="bg-green-600">
              {isSaving ? 'Saving...' : (editingCustomer ? 'Update' : 'Add')} Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Login Details Modal - Shows after creating customer */}
      <Dialog open={showLoginDetails} onOpenChange={setShowLoginDetails}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              ✅ Customer Created Successfully!
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">Share these login details with the customer:</p>
            <div className="bg-slate-100 p-4 rounded-lg space-y-3">
              <div>
                <Label className="text-xs text-slate-500">Username</Label>
                <p className="font-mono text-lg font-bold">{createdLoginDetails?.username}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Password</Label>
                <p className="font-mono text-lg font-bold">{createdLoginDetails?.password}</p>
              </div>
            </div>
            <p className="text-xs text-amber-600 mt-3">⚠️ Please save these details. The password cannot be retrieved later.</p>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => {
                navigator.clipboard.writeText(`Username: ${createdLoginDetails?.username}\nPassword: ${createdLoginDetails?.password}`);
                toast.success('Login details copied to clipboard!');
              }}
              variant="outline"
              className="mr-2"
            >
              📋 Copy
            </Button>
            <Button onClick={() => setShowLoginDetails(false)} className="bg-green-600">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Order Modal */}
      <Dialog open={isOrderModalOpen} onOpenChange={setIsOrderModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Order for {selectedCustomer?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Product Selection */}
            <div className="bg-slate-50 p-4 rounded-lg space-y-3">
              <Label className="font-semibold">Add Products</Label>
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-5">
                  <Select onValueChange={handleProductSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select product..." />
                    </SelectTrigger>
                    <SelectContent>
                      {products.filter(p => p.bodegaStock > 0).map(product => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name} ({product.bodegaStock} available)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Input
                    type="number"
                    placeholder="Qty"
                    value={newOrderItem.quantity || ''}
                    onChange={(e) => setNewOrderItem({...newOrderItem, quantity: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    type="number"
                    placeholder="Price"
                    value={newOrderItem.pricePerUnit || ''}
                    onChange={(e) => setNewOrderItem({...newOrderItem, pricePerUnit: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="col-span-3">
                  <Button onClick={handleAddOrderItem} className="w-full">
                    <Plus size={16} className="mr-1" /> Add
                  </Button>
                </div>
              </div>
            </div>

            {/* Order Items */}
            {orderForm.items.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-2">Product</th>
                      <th className="text-right p-2">Qty</th>
                      <th className="text-right p-2">Price</th>
                      <th className="text-right p-2">Total</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderForm.items.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">{item.name}</td>
                        <td className="text-right p-2">{item.quantity}</td>
                        <td className="text-right p-2">₱{item.pricePerUnit?.toLocaleString()}</td>
                        <td className="text-right p-2 font-medium">₱{item.total?.toLocaleString()}</td>
                        <td className="p-2">
                          <Button size="sm" variant="ghost" onClick={() => handleRemoveOrderItem(idx)}>
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t bg-green-50">
                      <td colSpan={3} className="p-2 font-semibold">Total Amount</td>
                      <td className="text-right p-2 font-bold text-green-600">₱{orderForm.totalAmount?.toLocaleString()}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Delivery Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Delivery Date</Label>
                <Input
                  type="date"
                  value={orderForm.deliveryDate}
                  onChange={(e) => setOrderForm({...orderForm, deliveryDate: e.target.value})}
                />
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Select value={orderForm.paymentTerms} onValueChange={(v) => setOrderForm({...orderForm, paymentTerms: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cod">Cash on Delivery</SelectItem>
                    <SelectItem value="credit">Credit (Pay Later)</SelectItem>
                    <SelectItem value="partial">Partial Payment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Delivery Address</Label>
              <Input
                value={orderForm.deliveryAddress}
                onChange={(e) => setOrderForm({...orderForm, deliveryAddress: e.target.value})}
                placeholder="Delivery address..."
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={orderForm.notes}
                onChange={(e) => setOrderForm({...orderForm, notes: e.target.value})}
                placeholder="Order notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOrderModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateOrder} disabled={isSaving || orderForm.items.length === 0} className="bg-green-600">
              {isSaving ? 'Creating...' : 'Create Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Modal */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment from {selectedCustomer?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedCustomer?.balance > 0 && (
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-sm text-amber-700">
                  Outstanding Balance: <span className="font-bold">₱{selectedCustomer.balance?.toLocaleString()}</span>
                </p>
              </div>
            )}
            <div>
              <Label>Amount (₱) *</Label>
              <Input
                type="number"
                value={paymentForm.amount || ''}
                onChange={(e) => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value) || 0})}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={paymentForm.paymentMethod} onValueChange={(v) => setPaymentForm({...paymentForm, paymentMethod: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference Number</Label>
              <Input
                value={paymentForm.referenceNumber}
                onChange={(e) => setPaymentForm({...paymentForm, referenceNumber: e.target.value})}
                placeholder="e.g., GCash ref, check number..."
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                placeholder="Payment notes..."
                rows={2}
              />
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                Payment will be marked as <span className="font-bold">Pending</span> and requires confirmation from Alma.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPaymentModalOpen(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment} disabled={isSaving || paymentForm.amount <= 0} className="bg-green-600">
              {isSaving ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Customer Details Modal */}
      <Dialog open={isViewCustomerOpen} onOpenChange={setIsViewCustomerOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{customerDetails?.name}</DialogTitle>
          </DialogHeader>
          {customerDetails && (
            <div className="space-y-6 py-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600">Total Orders</p>
                  <p className="text-2xl font-bold text-blue-700">₱{customerDetails.totalOrders?.toLocaleString()}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600">Total Paid</p>
                  <p className="text-2xl font-bold text-green-700">₱{customerDetails.totalPaid?.toLocaleString()}</p>
                </div>
                <div className={`p-4 rounded-lg ${customerDetails.balance > 0 ? 'bg-amber-50' : 'bg-slate-50'}`}>
                  <p className={`text-sm ${customerDetails.balance > 0 ? 'text-amber-600' : 'text-slate-600'}`}>Balance</p>
                  <p className={`text-2xl font-bold ${customerDetails.balance > 0 ? 'text-amber-700' : 'text-slate-700'}`}>
                    ₱{customerDetails.balance?.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                {customerDetails.phone && (
                  <div className="flex items-center gap-2">
                    <Phone size={16} className="text-slate-400" />
                    <span>{customerDetails.phone}</span>
                  </div>
                )}
                {customerDetails.address && (
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-slate-400" />
                    <span>{customerDetails.address}</span>
                  </div>
                )}
              </div>

              {/* Orders */}
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Package size={18} /> Recent Orders ({customerDetails.orders?.length || 0})
                </h3>
                {customerDetails.orders?.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {customerDetails.orders.map(order => (
                      <div key={order.id} className="p-3 bg-slate-50 rounded-lg flex justify-between items-center">
                        <div>
                          <p className="font-medium">₱{order.totalAmount?.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">
                            {order.items?.length} items • {new Date(order.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                          order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No orders yet</p>
                )}
              </div>

              {/* Payments */}
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <CreditCard size={18} /> Payment History ({customerDetails.payments?.length || 0})
                </h3>
                {customerDetails.payments?.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {customerDetails.payments.map(payment => (
                      <div key={payment.id} className="p-3 bg-slate-50 rounded-lg flex justify-between items-center">
                        <div>
                          <p className="font-medium">₱{payment.amount?.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">
                            {payment.paymentMethod} • {new Date(payment.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          payment.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                          payment.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {payment.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No payments recorded</p>
                )}
              </div>

              {/* Custom Pricing Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Tag size={18} className="text-purple-600" /> Custom Pricing ({customerPrices.length})
                  </h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowPricingSection(!showPricingSection)}
                    className="text-purple-600 border-purple-200"
                  >
                    {showPricingSection ? 'Hide' : 'Manage Prices'}
                  </Button>
                </div>
                
                {showPricingSection && (
                  <div className="space-y-3 p-3 bg-purple-50 rounded-lg">
                    {/* Add new custom price */}
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Label className="text-xs text-purple-600">Product</Label>
                        <Select 
                          value={newPriceForm.productId} 
                          onValueChange={(v) => {
                            const defaultPrice = getProductDefaultPrice(v);
                            setNewPriceForm({ productId: v, customPrice: defaultPrice });
                          }}
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue placeholder="Select product..." />
                          </SelectTrigger>
                          <SelectContent>
                            {products.map(product => (
                              <SelectItem key={product.id} value={product.id}>
                                {product.name} (Default: ₱{(product.sellingPricePerSack || product.wholesalePrice || 0).toLocaleString()})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-32">
                        <Label className="text-xs text-purple-600">Custom Price</Label>
                        <Input
                          type="number"
                          value={newPriceForm.customPrice}
                          onChange={(e) => setNewPriceForm({ ...newPriceForm, customPrice: e.target.value })}
                          className="bg-white"
                        />
                      </div>
                      <Button 
                        onClick={handleAddCustomPrice}
                        disabled={isSaving}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <Save size={16} />
                      </Button>
                    </div>

                    {/* List of custom prices */}
                    {customerPrices.length > 0 ? (
                      <div className="space-y-2">
                        {customerPrices.map(cp => (
                          <div key={cp.product_id} className="flex items-center justify-between p-2 bg-white rounded border">
                            <div>
                              <p className="font-medium text-sm">{getProductName(cp.product_id)}</p>
                              <p className="text-xs text-slate-500">
                                Default: ₱{getProductDefaultPrice(cp.product_id).toLocaleString()} → 
                                <span className="text-purple-600 font-semibold"> Custom: ₱{cp.custom_price?.toLocaleString()}</span>
                              </p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleRemoveCustomPrice(cp.product_id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-purple-600 text-sm text-center py-2">
                        No custom prices set. This customer uses default prices.
                      </p>
                    )}
                  </div>
                )}
                
                {!showPricingSection && customerPrices.length > 0 && (
                  <p className="text-sm text-purple-600">
                    {customerPrices.length} product(s) with custom pricing
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={async () => {
                try {
                  const response = await axios.get(`${API}/api/nas/reports/customer/statement/${customerDetails.id}`, {
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `statement_${customerDetails.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success('Statement downloaded');
                } catch (error) {
                  toast.error('Failed to download statement');
                }
              }}
              className="text-blue-600 border-blue-200"
            >
              <FileText size={16} className="mr-1" /> Download Statement
            </Button>
            <Button variant="outline" onClick={() => setIsViewCustomerOpen(false)}>Close</Button>
            <Button onClick={() => { setIsViewCustomerOpen(false); openOrderModal(customerDetails); }} className="bg-green-600">
              <Package size={16} className="mr-1" /> New Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Basket & Inventory Modal */}
      <Dialog open={isBasketModalOpen} onOpenChange={setIsBasketModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBasket className="text-purple-600" />
              {selectedCustomer?.name} - Basket & Inventory
            </DialogTitle>
          </DialogHeader>
          
          {customerBasket && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-3">
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-xs text-purple-600 font-medium">Total Orders</p>
                  <p className="text-2xl font-bold text-purple-700">{customerBasket.total_orders || 0}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600 font-medium">Total Items</p>
                  <p className="text-2xl font-bold text-blue-700">{customerBasket.total_items || 0}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-xs text-green-600 font-medium">Total Spent</p>
                  <p className="text-2xl font-bold text-green-700">₱{(customerBasket.total_spent || 0).toLocaleString()}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-xs text-red-600 font-medium">Outstanding</p>
                  <p className="text-2xl font-bold text-red-700">₱{(customerBasket.outstanding || 0).toLocaleString()}</p>
                </div>
              </div>

              {/* Pending Orders Section */}
              <PendingOrdersSection 
                customerId={selectedCustomer?.id} 
                customerName={selectedCustomer?.name}
                onOrderConfirmed={() => viewCustomerBasket(selectedCustomer)}
              />

              {/* Inventory Section */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-4 py-3 flex items-center gap-2">
                  <Warehouse size={18} className="text-slate-600" />
                  <h3 className="font-semibold text-slate-800">Current Inventory</h3>
                </div>
                <div className="p-4">
                  <InventorySection customerId={selectedCustomer?.id} />
                </div>
              </div>

              {/* Order History */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-4 py-3 flex items-center gap-2">
                  <Package size={18} className="text-slate-600" />
                  <h3 className="font-semibold text-slate-800">Order History (Delivered)</h3>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {customerBasket.basket_items?.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left px-4 py-2 text-xs font-semibold text-slate-600">Delivery Code</th>
                          <th className="text-left px-4 py-2 text-xs font-semibold text-slate-600">Items</th>
                          <th className="text-right px-4 py-2 text-xs font-semibold text-slate-600">Amount</th>
                          <th className="text-center px-4 py-2 text-xs font-semibold text-slate-600">Status</th>
                          <th className="text-left px-4 py-2 text-xs font-semibold text-slate-600">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {customerBasket.basket_items.map(item => (
                          <tr key={item.id} className="hover:bg-slate-50">
                            <td className="px-4 py-2 font-mono text-xs">{item.delivery_code}</td>
                            <td className="px-4 py-2">
                              {item.items?.map((i, idx) => (
                                <div key={idx} className="text-xs text-slate-600">
                                  {i.quantity}x {i.product_name}
                                </div>
                              ))}
                            </td>
                            <td className="px-4 py-2 text-right font-semibold">₱{(item.total_amount || 0).toLocaleString()}</td>
                            <td className="px-4 py-2 text-center">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                item.payment_status === 'paid' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {item.payment_status || 'unpaid'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-xs text-slate-500">
                              {item.delivered_at ? new Date(item.delivered_at).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center py-8 text-slate-500">No delivered orders yet</p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBasketModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Customer Credentials Modal */}
      <Dialog open={isCredentialsModalOpen} onOpenChange={setIsCredentialsModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="text-blue-600" />
              Customer Portal Access
            </DialogTitle>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-800">{selectedCustomer.name}</p>
                <p className="text-sm text-slate-500">{selectedCustomer.phone}</p>
                {selectedCustomer.has_portal_access && (
                  <div className="flex items-center gap-1 mt-2 text-green-600 text-sm">
                    <UserCheck size={14} />
                    Has portal access (Username: {selectedCustomer.username})
                  </div>
                )}
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm">
                <p className="font-medium text-blue-800">Customer Portal URL:</p>
                <p className="text-blue-600 font-mono text-xs mt-1">
                  {window.location.origin}/nas-portal/login
                </p>
              </div>
              
              <div>
                <Label>Username *</Label>
                <Input
                  value={credentialsForm.username}
                  onChange={(e) => setCredentialsForm({...credentialsForm, username: e.target.value})}
                  placeholder="e.g., maria.santos"
                />
              </div>
              
              <div>
                <Label>{selectedCustomer.has_portal_access ? 'New Password *' : 'Password *'}</Label>
                <Input
                  type="password"
                  value={credentialsForm.password}
                  onChange={(e) => setCredentialsForm({...credentialsForm, password: e.target.value})}
                  placeholder="Min 6 characters"
                />
              </div>
              
              {selectedCustomer.has_portal_access && (
                <div className="pt-2 border-t">
                  <Button 
                    variant="outline" 
                    className="w-full text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => {
                      handleRemoveCredentials(selectedCustomer);
                      setIsCredentialsModalOpen(false);
                    }}
                  >
                    Remove Portal Access
                  </Button>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCredentialsModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSetCredentials} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700">
              {isSaving ? 'Saving...' : selectedCustomer?.has_portal_access ? 'Update Credentials' : 'Set Credentials'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Inventory Section Component
const InventorySection = ({ customerId }) => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (customerId) {
      fetchInventory();
    }
  }, [customerId]);

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/nas-customer/admin/customer-inventory/${customerId}`);
      setInventory(response.data.inventory || []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4 text-slate-500">Loading inventory...</div>;
  }

  if (inventory.length === 0) {
    return <p className="text-center py-4 text-slate-500">No inventory yet. Items will appear here when orders are confirmed as delivered.</p>;
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {inventory.map(item => (
        <div key={item.id} className="p-3 bg-white rounded-lg border border-slate-200 hover:border-blue-300 transition-colors">
          <p className="font-medium text-slate-800 text-sm">{item.product_name}</p>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-slate-500">Current Stock:</span>
            <span className="text-lg font-bold text-blue-600">{item.stock} {item.unit}</span>
          </div>
          <p className="text-xs text-slate-400 mt-1">Total Received: {item.total_received}</p>
        </div>
      ))}
    </div>
  );
};

// Pending Orders Section Component - Shows orders awaiting delivery confirmation
const PendingOrdersSection = ({ customerId, customerName, onOrderConfirmed }) => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);

  useEffect(() => {
    if (customerId) {
      fetchPendingOrders();
    }
  }, [customerId]);

  const fetchPendingOrders = async () => {
    try {
      // Get all orders for this customer that are not yet delivered
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/nas-customer/admin/orders?status=`, {
        params: {}
      });
      // Filter to only show orders for this customer that need delivery confirmation
      const customerOrders = (response.data || []).filter(order => 
        order.customer_id === customerId && 
        ['pending', 'approved', 'released', 'out_for_delivery'].includes(order.status)
      );
      setPendingOrders(customerOrders);
    } catch (error) {
      console.error('Error fetching pending orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelivery = async (order) => {
    setConfirming(order.id);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/nas-customer/admin/orders/${order.id}/confirm-delivery-for-customer`,
        null,
        { params: { received_by: customerName, notes: 'Confirmed by admin' }}
      );
      toast.success(`Delivery confirmed for ${order.delivery_code}! Inventory updated.`);
      if (onOrderConfirmed) onOrderConfirmed();
      fetchPendingOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm delivery');
    } finally {
      setConfirming(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-blue-100 text-blue-700',
      released: 'bg-purple-100 text-purple-700',
      out_for_delivery: 'bg-orange-100 text-orange-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return null;
  }

  if (pendingOrders.length === 0) {
    return null;
  }

  return (
    <div className="border border-amber-300 rounded-lg overflow-hidden bg-amber-50">
      <div className="bg-amber-100 px-4 py-3 flex items-center gap-2">
        <Truck size={18} className="text-amber-600" />
        <h3 className="font-semibold text-amber-800">Pending Orders ({pendingOrders.length})</h3>
      </div>
      <div className="p-3 space-y-2">
        {pendingOrders.map(order => (
          <div key={order.id} className="bg-white p-3 rounded-lg border border-slate-200 flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-sm font-medium">{order.delivery_code}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadge(order.status)}`}>
                  {order.status?.replace('_', ' ')}
                </span>
              </div>
              <div className="text-xs text-slate-500">
                {order.items?.map((item, idx) => (
                  <span key={idx}>{idx > 0 ? ', ' : ''}{item.quantity}x {item.product_name}</span>
                ))}
              </div>
              <p className="text-sm font-semibold text-slate-700 mt-1">₱{(order.total_amount || 0).toLocaleString()}</p>
            </div>
            <Button 
              size="sm"
              onClick={() => handleConfirmDelivery(order)}
              disabled={confirming === order.id || order.status === 'pending'}
              className={order.status === 'pending' ? 'bg-slate-400' : 'bg-green-600 hover:bg-green-700'}
              data-testid={`confirm-delivery-${order.id}`}
            >
              {confirming === order.id ? (
                <RefreshCw size={14} className="animate-spin mr-1" />
              ) : (
                <CheckCircle size={14} className="mr-1" />
              )}
              {order.status === 'pending' ? 'Needs Approval' : 'Confirm Delivery'}
            </Button>
          </div>
        ))}
        <p className="text-xs text-amber-700 mt-2">
          * When you confirm delivery, items will be automatically added to the customer's inventory.
        </p>
      </div>
    </div>
  );
};

export default NASCustomers;
